// src/lib/api-client.ts
// Fetch wrapper with Bearer token injection — all API calls to the bridge
// server go through this client. Automatically attaches the Supabase JWT.
//
// Two interfaces exported:
//   apiClient.get/post/...  — non-throwing, returns { data, error }
//   api.get/post/...        — throwing, returns T directly (legacy-compatible)

import { supabase } from './supabase'
import { clientEnv } from '@/src/env'

const BASE_URL = clientEnv.VITE_BRIDGE_URL ?? ''

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

interface ApiResponse<T = unknown> {
  data?: T
  error?: { code: string; message: string }
}

// ---------------------------------------------------------------
// Error class (compatible with legacy lib/api-client ApiClientError)
// ---------------------------------------------------------------

/**
 * Error thrown when an API request fails (non-2xx response).
 * Contains the error code from the bridge's error envelope.
 */
export class ApiClientError extends Error {
  public readonly code: string

  constructor(code: string, message: string) {
    super(message)
    this.name = 'ApiClientError'
    this.code = code
    Object.setPrototypeOf(this, ApiClientError.prototype)
  }
}

// ---------------------------------------------------------------
// Core request function (non-throwing)
// ---------------------------------------------------------------

/**
 * Make an authenticated API request to the bridge server.
 * Automatically injects the Bearer token from the current Supabase session.
 */
async function request<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`
  }

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`

  const response = await fetch(url, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  })

  // Handle 204 No Content (successful delete, etc.)
  if (response.status === 204) {
    return { data: undefined as T }
  }

  if (!response.ok) {
    const errorBody = await response.json().catch(() => null)
    return {
      error: errorBody?.error ?? {
        code: 'REQUEST_FAILED',
        message: `Request failed with status ${response.status}`,
      },
    }
  }

  const data = await response.json()
  return data as ApiResponse<T>
}

// ---------------------------------------------------------------
// Non-throwing client (returns { data, error })
// ---------------------------------------------------------------

/** Authenticated API client — non-throwing, returns { data, error }. */
export const apiClient = {
  get: <T = unknown>(path: string, options?: ApiRequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),

  patch: <T = unknown>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T = unknown>(path: string, options?: ApiRequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

// ---------------------------------------------------------------
// Throwing fetch wrapper (legacy-compatible)
// ---------------------------------------------------------------

/**
 * Typed fetch wrapper that throws ApiClientError on failure.
 * Returns the response data directly (unwrapped from the { data } envelope).
 * Compatible with the legacy lib/api-client fetchApi interface.
 */
export async function fetchApi<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const result = await request<T>(path, options)
  if (result.error) {
    throw new ApiClientError(result.error.code, result.error.message)
  }
  return result.data as T
}

// ---------------------------------------------------------------
// Throwing convenience client (legacy-compatible)
// ---------------------------------------------------------------

/**
 * Authenticated API client — throws on error, returns T directly.
 * Drop-in replacement for the legacy lib/api-client api object.
 */
export const api = {
  get: <T = unknown>(path: string, options?: ApiRequestOptions) =>
    fetchApi<T>(path, { ...options, method: 'GET' }),

  post: <T = unknown>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    fetchApi<T>(path, { ...options, method: 'POST', body }),

  patch: <T = unknown>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    fetchApi<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T = void>(path: string, options?: ApiRequestOptions) =>
    fetchApi<T>(path, { ...options, method: 'DELETE' }),
}
